/* @flow */

import assert from 'assert';
import { navigate } from 'gatsby';
import merge from 'lodash/merge';
import React from 'react';
import store from 'store/dist/store.modern';
import { Container, Subscribe, Provider as UnstatedProvider } from 'unstated';

import { AuthContext } from '../components/AuthWrapper';
import Query from '../components/Query';
import PayReservationMutation from '../mutations/PayReservation';
import SetReservationCart from '../mutations/SetReservationCart';
import paths from '../paths';

const KEY = '@@bni/reservation';

const initialState = {
  plan: undefined,
  cloudDicom: null,
  numDevicesLightning: null,
  numDevicesUsbC: null,
  numCloudUsers: null,
  warrantyYears: 1,
  cloudSubscriptionYears: 1,
};

export class ReservationContainer extends Container {
  constructor() {
    super();

    this.state = store.get(KEY) || initialState;
  }

  init(client, reservation, email, deviceOnlyReservationToken) {
    this.client = client;
    this.email = email;
    this.deviceOnlyToken = deviceOnlyReservationToken;

    this.setRemote(reservation);
  }

  clearCart() {
    if (this.remote.payment || !this.remote.cart) return;

    delete this.remote.cart;
    this.forceUpdate();
  }

  forceUpdate() {
    return this.setState({});
  }

  async testingResetReservation() {
    await this.client.commitMutation(
      /* GraphQL */ `
        mutation($input: ResetReservationInput!) {
          resetReservation(input: $input) {
            reservation {
              id
            }
          }
        }
      `,
      {
        input: { reservationId: this.remote.id, token: this.deviceOnlyToken },
      },
    );

    store.remove(KEY);
    window.location.assign('/');
  }

  async payReservation(payment) {
    const { payReservation } = await PayReservationMutation.commit(
      this.client,
      this.remote.id,
      { ...payment, token: this.deviceOnlyToken },
    );

    merge(this.remote, payReservation.reservation);
  }

  async setReservationCart(shippingAddress, shippingMethod) {
    const {
      plan,
      numDevicesLightning,
      numDevicesUsbC,
      numCloudUsers,
      cloudSubscriptionYears,
      warrantyYears,
      cloudDicom,
    } = this.state;

    const { setReservationCart } = await SetReservationCart.commit(
      this.client,
      this.remote.id,
      {
        shippingAddress,
        shippingMethod: shippingMethod || 'STANDARD_SHIPPING',
        plan,
        numDevicesLightning,
        numDevicesUsbC,
        numCloudUsers,
        cloudSubscriptionYears,
        warrantyYears,
        cloudDicom,
        token: this.deviceOnlyToken,
      },
    );

    merge(this.remote, setReservationCart.reservation);

    await this.forceUpdate();
  }

  setState(updateOrFunc) {
    return super.setState(updateOrFunc, () => {
      store.set(KEY, this.state);
    });
  }

  get shippingPrice() {
    return this.remote.cart ? this.remote.cart.shippingPrice : null;
  }

  get taxPrice() {
    return this.remote.cart ? this.remote.cart.taxPrice : null;
  }

  get devicePrice() {
    const { inventory, cart } = this.remote;
    return cart ? cart.devicesPrice / cart.numDevices : inventory.devicePrice;
  }

  get devicesLightningPrice() {
    const { cart, inventory } = this.remote;
    return cart
      ? cart.devicesLightningPrice || 0
      : this.numDevicesLightning * inventory.devicePrice;
  }

  get devicesUsbCPrice() {
    const { cart, inventory } = this.remote;
    return cart
      ? cart.devicesUsbCPrice || 0
      : this.numDevicesUsbC * inventory.devicePrice;
  }

  get devicesPrice() {
    return this.devicesLightningPrice + this.devicesUsbCPrice;
  }

  get cloudTeamBasePricePerMonth() {
    const {
      cloudTeamBasePricePerMonth,
      teamBaseNumCloudUsers,
    } = this.remote.inventory;
    return cloudTeamBasePricePerMonth * teamBaseNumCloudUsers;
  }

  get cloudTeamAdditionalPricePerMonth() {
    const { cloudTeamAdditionalPricePerMonth } = this.remote.inventory;
    return cloudTeamAdditionalPricePerMonth;
  }

  get cloudUserPricePerMonth() {
    const { inventory } = this.remote;

    return inventory.cloudIndividualPricePerMonth;
  }

  get cloudTeamBasePricePerYear() {
    return this.cloudTeamBasePricePerMonth * 12;
  }

  get cloudTeamAdditionalPricePerYear() {
    return this.cloudTeamAdditionalPricePerMonth * 12;
  }

  get cloudUserPricePerYear() {
    return this.cloudUserPricePerMonth * 12;
  }

  get cloudUsersPrice() {
    const { cart } = this.remote;
    if (cart) return cart.cloudUsersPrice;

    return this.plan === 'TEAM'
      ? this.additionalCloudUsersPrice + this.baseCloudUsersPrice
      : this.numCloudUsers *
          this.cloudUserPricePerMonth *
          this.cloudSubscriptionYears *
          12;
  }

  get baseCloudUsersPrice() {
    return this.remote.cart
      ? this.remote.cart.baseCloudUsersPrice
      : this.cloudTeamBasePricePerMonth * this.cloudSubscriptionYears * 12;
  }

  get additionalCloudUsersPrice() {
    return this.remote.cart
      ? this.remote.cart.additionalCloudUsersPrice
      : this.additionalNumCloudUsers *
          this.cloudTeamAdditionalPricePerYear *
          this.cloudSubscriptionYears;
  }

  get warrantyPrice() {
    const { inventory } = this.remote;
    let price = 0;
    if (this.warrantyYears === 2) price = inventory.extendedWarranty2YearPrice;
    if (this.warrantyYears === 3) price = inventory.extendedWarranty3YearPrice;
    return price;
  }

  get extendedWarrantiesPrice() {
    const { cart } = this.remote;
    return cart
      ? cart.extendedWarrantiesPrice
      : this.warrantyPrice * (this.numDevicesLightning + this.numDevicesUsbC);
  }

  get subtotal() {
    return (
      this.extendedWarrantiesPrice + this.cloudUsersPrice + this.devicesPrice
    );
  }

  get total() {
    return this.remote.cart ? this.remote.cart.totalPrice : this.subtotal;
  }

  get numCloudUsers() {
    return this.remote.cart
      ? this.remote.cart.numCloudUsers
      : this.state.numCloudUsers;
  }

  get baseNumCloudUsers() {
    return this.remote.cart
      ? this.remote.cart.baseNumCloudUsers
      : this.minCloudUsers;
  }

  get additionalNumCloudUsers() {
    return this.remote.cart
      ? this.remote.cart.additionalNumCloudUsers
      : this.state.numCloudUsers - this.minCloudUsers;
  }

  get numDevices() {
    return (this.numDevicesLightning || 0) + (this.numDevicesUsbC || 0);
  }

  get numDevicesLightning() {
    return this.remote.cart
      ? this.remote.cart.numDevicesLightning
      : this.state.numDevicesLightning;
  }

  get numDevicesUsbC() {
    return this.remote.cart
      ? this.remote.cart.numDevicesUsbC
      : this.state.numDevicesUsbC;
  }

  get billingAddress() {
    return this.remote.payment && this.remote.payment.billingAddress;
  }

  get shippingAddress() {
    return this.remote.cart && this.remote.cart.shippingAddress;
  }

  get plan() {
    return this.state.plan;
  }

  get maxCloudUsers() {
    const { maxCloudUsers } = this.remote.inventory;
    return maxCloudUsers;
  }

  get minCloudUsers() {
    const { teamBaseNumCloudUsers } = this.remote.inventory;
    return this.plan === 'TEAM' ? teamBaseNumCloudUsers : 1;
  }

  async setQuantities(numCloudUsers, numDevicesLightning, numDevicesUsbC) {
    await this.setState({
      numCloudUsers,
      numDevicesLightning,
      numDevicesUsbC,
      cloudDicom: null,
    });
  }

  async setPlan(plan) {
    assert(plan === 'TEAM' || plan === 'INDIVIDUAL' || plan === 'NONE');

    const planIsTeam = plan === 'TEAM';
    const { teamBaseNumCloudUsers } = this.remote.inventory;

    let numCloudUsers = 0;
    if (plan === 'INDIVIDUAL') numCloudUsers = 1;
    if (planIsTeam) numCloudUsers = teamBaseNumCloudUsers;

    await this.setState({
      ...initialState,
      plan,
      numCloudUsers,
      cloudDicom: null,
      numDevicesLightning: plan === 'NONE' ? 1 : 0,
      numDevicesUsbC: 0,
    });
  }

  async setRemote(reservation) {
    this.remote = reservation;
    if (reservation.isDeviceOnly && this.plan !== 'NONE') {
      await this.setPlan('NONE');
      this.cloudDicom = false;
    }
  }
}

['cloudDicom', 'cloudSubscriptionYears', 'warrantyYears'].forEach(stateProp =>
  Object.defineProperty(ReservationContainer.prototype, stateProp, {
    enumerable: false,
    get() {
      const { cart } = this.remote;
      return cart ? cart[stateProp] : this.state[stateProp];
    },
    set(value) {
      this.setState({ [stateProp]: value });
    },
  }),
);

const reservationFragment = /* GraphQL */ `
  id
  isPaid
  isDeviceOnly
  inventory {
    cloudIndividualPricePerMonth
    cloudTeamBasePricePerMonth
    cloudTeamAdditionalPricePerMonth
    extendedWarranty2YearPrice
    extendedWarranty3YearPrice
    devicePrice
    teamBaseNumCloudUsers
    maxDevices
    maxCloudUsers
    teamBaseNumCloudUsers
  }
  cart {
    plan
    numDevices
    numDevicesUsbC
    numDevicesLightning
    cloudSubscriptionYears
    warrantyYears
    cloudDicom
    shippingAddress {
      name
      line1
      line2
      city
      state
      postalCode
      country
      phone
      attention
    }
    shippingMethod
    devicesPrice
    devicesUsbCPrice
    devicesLightningPrice
    additionalCloudUsersPrice
    baseCloudUsersPrice
    baseNumCloudUsers
    additionalNumCloudUsers
    cloudUsersPrice
    extendedWarrantiesPrice
    shippingPrice
    taxPrice
    totalPrice
  }
  payment {
    quoteRequested
    billingAddress {
      name
      line1
      line2
      city
      state
      postalCode
      country
      phone
    }
    source {
      brand
      last4
      expMonth
      expYear
      tokenizationMethod
    }
  }
`;
const query = /* GraphQL */ `
  query {
    viewer {
      id
      email
      profile {
        name
      }
      reservation {
        ${reservationFragment}
      }
    }
  }
`;

const deviceOnlyQuery = /* GraphQL */ `
  query DeviceOnlyQuery($token: String!) {
    reservationByToken(token: $token) {
      ${reservationFragment}
    }
    reservationInfoByToken(token: $token) {
      email
    }
  }
`;

export const container = new ReservationContainer();

function renderError(error) {
  if (Array.isArray(error.details)) {
    for (const { upstream } of error.details) {
      if (upstream && upstream.status === 409) {
        for (const upstreamError of upstream.errors) {
          if (
            upstreamError &&
            upstreamError.code === 'invalid_reservation.is_expired'
          ) {
            navigate(paths.invitationExpired);
            return <span />;
          }
        }
      }
    }
  }
  return null;
}

let zendeskIdentitySet = false;
function updateZendesk(data) {
  if (!window.zE || zendeskIdentitySet) return;

  const email = data.viewer?.email || data.reservationInfoByToken.email;
  const name = data.viewer?.profile?.name || email.split('@')[0];

  if (name && email) {
    window.zE(() => {
      // Overly defensive
      if (!window.zE.identify) return;

      window.zE.identify({
        name,
        email,
      });
      zendeskIdentitySet = true;
    });
  }
}

export function ReservationProvider({ children }) {
  return (
    <AuthContext.Consumer>
      {({ tokenResponse }) =>
        tokenResponse.deviceOnlyReservationToken ? (
          <Query
            query={deviceOnlyQuery}
            renderError={renderError}
            variables={{ token: tokenResponse.deviceOnlyReservationToken }}
          >
            {(data, client) => {
              updateZendesk(data);
              container.init(
                client,
                data.reservationByToken,
                data.reservationInfoByToken.email,
                tokenResponse.deviceOnlyReservationToken,
              );
              return (
                <UnstatedProvider inject={[container]}>
                  {children}
                </UnstatedProvider>
              );
            }}
          </Query>
        ) : (
          <Query query={query} renderError={renderError}>
            {(data, client) => {
              updateZendesk(data);
              container.init(
                client,
                data.viewer.reservation,
                data.viewer.email,
              );

              return (
                <UnstatedProvider inject={[container]}>
                  {children}
                </UnstatedProvider>
              );
            }}
          </Query>
        )
      }
    </AuthContext.Consumer>
  );
}

export default function withReservation(Component) {
  return React.forwardRef((props, ref) => (
    <Subscribe to={[ReservationContainer]}>
      {cart => <Component ref={ref} {...props} cart={cart} />}
    </Subscribe>
  ));
}
